/**
 * Exclusive (XOR) gateways with a separate End path do not need a converge/join:
 * only one path runs, so continuing branches never merge with each other or the End.
 *
 * Cases covered:
 *  - 1 continuing branch + ≥1 End  → no converge
 *  - 2+ continuing branches + ≥1 End → no converge between those branches
 */

const normalizeType = (type) => String(type || "").trim().toUpperCase();

const branchTarget = (b) => b?.targetTaskId ?? b?.target_task_id ?? "";

/** Branch continues into a task or another gateway. */
export function isContinuingBranch(branch) {
  const t = branchTarget(branch);
  return t != null && String(t) !== "";
}

/**
 * Branch terminates at an End event (named endEventName, or no task target).
 * Explicit End Events on the gateway also count via `endpoints`.
 */
export function isEndBranch(branch) {
  if (branch?.endEventName) return true;
  return !isContinuingBranch(branch);
}

export function countContinuingBranches(gateway) {
  return (gateway?.branches || []).filter(isContinuingBranch).length;
}

export function countEndPaths(gateway) {
  const endBranches = (gateway?.branches || []).filter(isEndBranch).length;
  const endpoints = Array.isArray(gateway?.endpoints)
    ? gateway.endpoints.length
    : 0;
  return endBranches + endpoints;
}

/**
 * True when Exclusive has ≥1 continuing branch and ≥1 separate End path —
 * converge/join must not be required or emitted.
 */
export function exclusiveSkipsConvergence(gateway) {
  if (normalizeType(gateway?.type) !== "EXCLUSIVE") return false;
  return countContinuingBranches(gateway) >= 1 && countEndPaths(gateway) >= 1;
}

/** Cleared converge fields for gateways that must not emit a join. */
export const EMPTY_CONVERGE_FIELDS = {
  convergeAtTaskId: "",
  convergeGatewayName: "",
  convergeToEnd: false,
  convergeAtGatewayId: "",
};

export function clearConvergeFields(gateway) {
  return { ...gateway, ...EMPTY_CONVERGE_FIELDS };
}

/**
 * Whether converge (join) is required for this gateway.
 * Exclusive continue+End patterns are excluded.
 */
export function gatewayRequiresConvergence(gateway, needsConvergenceFn) {
  const type = gateway?.type;
  if (typeof needsConvergenceFn === "function") {
    if (!needsConvergenceFn(type)) return false;
  } else {
    const n = normalizeType(type);
    if (
      n !== "PARALLEL" &&
      n !== "INCLUSIVE" &&
      n !== "EXCLUSIVE" &&
      n !== "COMPLEX"
    ) {
      return false;
    }
  }
  if (exclusiveSkipsConvergence(gateway)) return false;
  return true;
}
